import type { RMapLongInteger } from "@repo/codegen/teashop-merchant-api"
import type { AxiosResponse } from "axios"
import type { Ref } from "vue"
import type { TableCartSnapshot } from "@/api/business/table"
import type * as Product from "@/api/business/types/product"
import type * as Table from "@/api/business/types/table"
import { ElMessage } from "element-plus"
import { cloneDeep, debounce } from "lodash-es"
import { defineStore } from "pinia"
import { computed, ref } from "vue"
import { getCategoryAndProductListByShopId, getCategoryTreeWithProductListByShopId } from "@/api/business/product"
import {
  getOpeningTableComsumeDetailByRecord,
  getOpeningTableConsumeDetailListByShopId,
  getOpeningTableConsumeDetailListByShopIdV2,
  getOpeningTableInfoV2,
  getOpeningTableListByShopId,
  getTableCartSnapshot,
  getTableCategoryListByShopId,
  getTableCategoryListV2,
  getTableOpeningReservation,
  setTableCartSnapshot,
} from "@/api/business/table"
import { getTableRuntimeSnapshot } from "@/api/business/tableRuntime"
import { merchantPmsItemCategoryAPI } from "@/api/teashop-merchant"
import { getEffectiveCommissionEmployeeList } from "@/api/user/worker"
import { useUserStore } from "@/store/modules/user"
import { TableStatusConstants } from "@/types/tableStatusConstants"
import { normalizeOpaqueLongId } from "@/utils/opaqueLongId"
import { getPreOpenCardTotalFee, isPreOpenTable } from "@/utils/preOpenTable"
import { flattenProductSecondCategories, normalizeProductCategoryTree } from "@/utils/productCategoryTree"
import { BUSINESS_TABLE_ACTION_PERMISSIONS } from "@/utils/roleFeatureAccess"
import {
  getReservationOpenTableContext,
  getReservationReservedTableId,
  RESERVATION_OPEN_TABLE_STALE_MESSAGE,
} from "@/views/page/business/components/Booking/reservationOpenTable"
import { TableRuntimeProjection } from "./tableRuntimeProjection"

type TableRelationSource = {
  tableMovingRecordId?: number | null
  tableRelationType?: string | null
  tableRelationBadges?: Table.TableRelationBadgeInfo[] | null
  tableTransferBatchList?: Table.TableTransferBatchInfo[] | null
}

type TableCartSyncBatch = {
  generation: number
  tableRecordId: string | number
  productList: Product.MerchantTableProductVO[]
  timer: ReturnType<typeof setTimeout> | null
  promise: Promise<TableCartSnapshot>
  resolve: (snapshot: TableCartSnapshot) => void
  reject: (error: unknown) => void
}

type TableCartSyncController = {
  generation: number
  pending?: TableCartSyncBatch
  active?: Promise<TableCartSnapshot>
  activeTableRecordId?: string
  serial: Promise<void>
}

function firstDefined<T>(...values: Array<T | null | undefined>): T | undefined {
  return values.find((value): value is T => value !== undefined && value !== null)
}

function isNonEmptyArray<T>(value: T[] | null | undefined): value is T[] {
  return Array.isArray(value) && value.length > 0
}

function normalizeOpeningTableListPayload(
  payload: Table.OpeningTableListPayload | null | undefined,
): Table.MerchantOpeningTableDTO[] {
  if (Array.isArray(payload)) {
    return payload
  }
  if (Array.isArray(payload?.openTableList)) {
    return payload.openTableList
  }
  return []
}

function normalizeTableOpeningReservationPayload(
  payload: Table.TableReservationDTO[] | null | undefined,
  fallback: Table.TableReservationDTO[] = [],
): Table.TableReservationDTO[] {
  return Array.isArray(payload) ? payload : fallback
}

function isSameTableTransferBatch(
  left: Table.TableTransferBatchInfo,
  right: Table.TableTransferBatchInfo,
) {
  if (left.transferBatchId && right.transferBatchId) {
    return String(left.transferBatchId) === String(right.transferBatchId)
  }
  return Boolean(left.sourceTableRecordId && right.sourceTableRecordId
    && left.targetTableRecordId && right.targetTableRecordId
    && String(left.sourceTableRecordId) === String(right.sourceTableRecordId)
    && String(left.targetTableRecordId) === String(right.targetTableRecordId))
}

function mergeDefinedTableTransferBatch(
  base: Table.TableTransferBatchInfo,
  patch: Table.TableTransferBatchInfo,
) {
  const result = { ...base }
  ;(Object.keys(patch) as Array<keyof Table.TableTransferBatchInfo>).forEach((key) => {
    const value = patch[key]
    if (value !== undefined && value !== null) {
      result[key] = value as never
    }
  })
  return result
}

function mergeTableTransferBatchList(
  consumeList: Table.TableTransferBatchInfo[] | null | undefined,
  openingList: Table.TableTransferBatchInfo[] | null | undefined,
) {
  if (!isNonEmptyArray(consumeList)) {
    return isNonEmptyArray(openingList) ? openingList : firstDefined(consumeList, openingList)
  }
  if (!isNonEmptyArray(openingList)) {
    return consumeList
  }
  const result = [...openingList]
  consumeList.forEach((consumeBatch) => {
    const matchedIndex = result.findIndex(openingBatch => isSameTableTransferBatch(openingBatch, consumeBatch))
    if (matchedIndex >= 0) {
      result[matchedIndex] = mergeDefinedTableTransferBatch(result[matchedIndex], consumeBatch)
      return
    }
    result.push(consumeBatch)
  })
  return result
}

export const useTableStore = defineStore("table", () => {
  type CommissionEmployeeOption = {
    id: number
    name: string
  }

  const openTableDialogVisible = ref(false)
  const currentOpenTableInfo = ref<Table.MerchantOpeningTableDTO>()
  const tableReservationFields = ref<Table.UnionReservation>()
  const tableContentLoading = ref(false)
  const tableOpeningReservationList = ref<Table.TableReservationDTO[]>([])
  const consumeDetail = ref<Record<string, Table.TableItemConsumeInfo>>()
  const tableCategoryListV2Cache = ref<Table.TableCategoryDTO[]>()
  const activeStatus = ref(0)
  const refreshCount = ref(0)
  const refreshTableMsgList = ref<any[]>([])
  const refreshReservationFlag = ref(false)
  // 是否是首次加载
  const isFirstLoad = ref(true)

  const refreshHandler = () => {
    fetchOpeningTableListV2()
    // 如果当前有桌台详情抽屉打开，则同时刷新桌台详情，更新该信号量即可
    setRefreshCount(getRefreshCount().value + 1)
  }

  const debounceRefresh = debounce(
    () => {
      refreshHandler()
    },
    10000,
    {
      leading: false,
      trailing: true,
      maxWait: 1000,
    },
  )

  const enhancedDebounceRefresh = () => {
    debounceRefresh()
    setTimeout(() => {
      // 替代了isForce，因为最后一次消息总是被执行
      debounceRefresh.flush()
    }, 1000)
  }

  const setRefreshReservationFlag = (value: boolean) => {
    refreshReservationFlag.value = value
  }

  const handleOpenTable = (openTableReservationInfo?: Table.UnionReservation) => {
    const reservationContext = getReservationOpenTableContext(openTableReservationInfo)
    const reserveTableId = getReservationReservedTableId(openTableReservationInfo)
    if (openTableReservationInfo && (!reservationContext || !reserveTableId)) {
      ElMessage.warning(RESERVATION_OPEN_TABLE_STALE_MESSAGE)
      setRefreshReservationFlag(true)
      enhancedDebounceRefresh()
      return
    }
    if (!reserveTableId) {
      return
    }
    currentOpenTableInfo.value = openingTableList.value.find(
      (item) => item.tableId === reserveTableId,
    )

    if (!currentOpenTableInfo.value) {
      ElMessage.warning(RESERVATION_OPEN_TABLE_STALE_MESSAGE)
      setRefreshReservationFlag(true)
      enhancedDebounceRefresh()
      return
    }
    tableReservationFields.value = openTableReservationInfo
    openTableDialogVisible.value = true
  }

  const setRefreshCount = (value: number) => {
    refreshCount.value = value
  }

  const getRefreshCount = () => computed(() => refreshCount.value)

  const addRefreshTableMsg = (message: any) => {
    if (refreshTableMsgList.value.length > 10) {
      refreshTableMsgList.value.shift()
    }
    refreshTableMsgList.value.push(message)
  }

  const getRefreshTableMsgList = () => computed(() => refreshTableMsgList.value)

  /**
   * 获取桌台消费明细 - 辅助数据
   * @param tableRecordId 桌台使用记录id
   */
  const fetchOpeningTableComsumeDetailByRecord = async (tableRecordId: string | number) => {
    const res = await getOpeningTableComsumeDetailByRecord(tableRecordId)
    consumeDetail.value = res.data
  }

  const getConsumeDetail = (tableRecordId: number) => computed(() => consumeDetail.value?.[tableRecordId])

  const getConsumeDetailRecord = () => computed(() => consumeDetail.value)

  const openingTableConsumeDetailList = ref<Table.TableItemConsumeInfoTuple[]>([])

  /**
   * 获取当前商户的所有消费明细信息 - 辅助数据
   * merchant/table/opening/consume/total
   */
  const fetchOpeningTableConsumeDetailList = async () => {
    const res = await getOpeningTableConsumeDetailListByShopId()
    openingTableConsumeDetailList.value = res.data || []
  }

  const getOpeningTableConsumeDetailList = () => computed(() => openingTableConsumeDetailList.value)

  const getOpeningTableConsumeDetailByTableRecordId = (tableRecordId: number) =>
    computed(() => openingTableConsumeDetailList.value.find((item) => item.tableRecordId === tableRecordId))

  const getOpeningTableTotalFee = (tableRecordId: number) =>
    computed(() => openingTableList.value.find((item) => item.tableRecordId === tableRecordId)?.totalFee)

  const getOpeningTableOrderFlag = (tableRecordId: number) =>
    computed(() => openingTableList.value.find((item) => item.tableRecordId === tableRecordId)?.orderFlag)

  const userStore = useUserStore()
  const shopId = userStore.shopId
  const getActiveShopId = () => userStore.shopId || shopId
  const runtimeProjection = new TableRuntimeProjection()
  let runtimeRequestEpoch = 0

  const resetRuntimeProjection = () => {
    runtimeRequestEpoch += 1
    runtimeProjection.reset()
  }

  const fetchRuntimeProjection = async (freshMetadata = false) => {
    const requestEpoch = ++runtimeRequestEpoch
    const listEpoch = openingTableListRequestEpoch
    const requestedShop = normalizeOpaqueLongId(getActiveShopId())
    if (!requestedShop) return false
    try {
      const response = await getTableRuntimeSnapshot()
      if (requestEpoch !== runtimeRequestEpoch || !isLatestOpeningTableListRequest(listEpoch)
        || requestedShop !== normalizeOpaqueLongId(getActiveShopId())) return true
      const result = runtimeProjection.accept(response.data.data, requestedShop, openingTableList.value, freshMetadata)
      if (result.kind === "applied") openingTableList.value = result.tables!
      if (result.kind === "refresh") runtimeProjection.reset()
      return result.kind !== "refresh"
    } catch {
      if (requestEpoch !== runtimeRequestEpoch || !isLatestOpeningTableListRequest(listEpoch)
        || requestedShop !== normalizeOpaqueLongId(getActiveShopId())) return true
      if (requestEpoch === runtimeRequestEpoch) runtimeProjection.reset()
      return false
    }
  }

  const refreshRuntimeOrLegacy = async () => {
    if (!await fetchRuntimeProjection()) {
      await fetchOpeningTableListV2()
    }
    setRefreshCount(getRefreshCount().value + 1)
  }

  const applyRuntimeMessage = (payload: unknown) => {
    let value: unknown
    try {
      value = typeof payload === "string" ? JSON.parse(payload) : payload
    } catch {
      return false
    }
    if (!value || typeof value !== "object" || !("protocolVersion" in value)) return false
    runtimeRequestEpoch += 1
    const result = runtimeProjection.accept(value, normalizeOpaqueLongId(getActiveShopId()) ?? "", openingTableList.value)
    if (result.kind === "applied") openingTableList.value = result.tables!
    if (result.kind === "refresh") {
      runtimeProjection.reset()
      enhancedDebounceRefresh()
    }
    return true
  }
  // 初始数据，用于骨架屏，后续统一管理
  const openingTableListSkeleton: Ref<Table.MerchantOpeningTableDTO[]> = ref([
    ...Array.from({ length: 6 }, (_, index) => ({
      tableId: index + 1,
      tableCategoryId: index < 4 ? 1 : 2,
      tableName: `xxx${index + 1}`,
      status: 0,
      sort: index + 1,
    })),
  ])
  const tableCategoryListSkeleton: Ref<Table.TableCategoryType[]> = ref([
    ...Array.from({ length: 2 }, (_, index) => ({
      tableCategoryId: index + 1,
      categoryName: `xx${index + 1}`,
      shopId,
      sort: index + 1,
      categoryStatus: 1,
      defaultBillingRuleId: index + 1,
    })),
  ])
  const tableCategoryList: Ref<Table.TableCategoryType[]> = ref(tableCategoryListSkeleton.value)
  const tableCategoryListAll = computed<Table.TableCategoryType[]>(() => {
    return [
      {
        tableCategoryId: 0,
        categoryName: "全部",
        shopId,
        sort: 0,
        categoryStatus: 1,
        defaultBillingRuleId: 0,
      },
      ...[...tableCategoryList.value].sort((a: any, b: any) => a?.sort - b?.sort),
    ]
  })
  const openingTableList: Ref<Table.MerchantOpeningTableDTO[]> = ref(openingTableListSkeleton.value)
  const openingTableVoList: Ref<Table.MerchantOpeningTableVO[]> = ref([])
  // 所有后端的桌台信息，映射openingTableList为categoryId分类的Map
  const openingTableListMap = computed(() => {
    return openingTableList.value.reduce(
      (acc, item) => {
        const cateId = item.tableCategoryId!
        if (!acc[cateId]) {
          acc[cateId] = []
        }
        acc[cateId].push(item)
        return acc
      },
      {
        // 全部分类下的桌台
        "0": openingTableList.value,
      } as Record<string, Table.MerchantOpeningTableDTO[]>,
    )
  })
  const tableStatusList = ref<Table.TableStatusType[]>([
    {
      label: "全部",
      value: 0,
      tableCount: 0,
      color: "#00000099",
      iconColor: "#00000099",
      textColor: "white",
      show: true,
      simple: "全",
    },
    {
      label: "空闲中",
      value: 1,
      tableCount: 0,
      color: "var(--tea-component-business-table-free-bg-color)",
      iconColor: "var(--tea-component-business-table-free-icon-color)",
      textColor: "var(--tea-component-business-table-free-text-color)",
      borderColor: "var(--tea-component-business-table-free-border-color)",
      show: true,
      simple: "空",
      footerColor: "var(--tea-component-business-table-free-footer-bg-color)",
      activeBorderColor: "var(--tea-component-business-table-free-active-shadow-color)",
    },
    {
      label: "待下单",
      value: 2,
      tableCount: 0,
      color: "var(--tea-component-business-table-status-wait-bg-color)",
      iconColor: "var(--tea-component-business-table-status-wait-accent-color)",
      textColor: "var(--tea-component-business-table-status-wait-text-color)",
      show: true,
      simple: "待",
      footerColor: "var(--tea-component-business-table-status-wait-footer-bg-color)",
      activeBorderColor: "var(--tea-component-business-table-status-wait-active-shadow-color)",
    },
    {
      label: "消费中",
      value: 3,
      tableCount: 0,
      color: "var(--tea-component-business-table-status-consuming-bg-color)",
      iconColor: "var(--tea-component-business-table-status-consuming-accent-color)",
      textColor: "var(--tea-component-business-table-status-consuming-text-color)",
      show: true,
      simple: "消",
      footerColor: "var(--tea-component-business-table-status-consuming-footer-bg-color)",
      activeBorderColor: "var(--tea-component-business-table-status-consuming-active-shadow-color)",
    },
    {
      label: "已预订",
      value: 4,
      tableCount: 0,
      color: "var(--tea-component-business-table-status-booked-bg-color)",
      iconColor: "var(--tea-component-business-table-status-booked-accent-color)",
      textColor: "var(--tea-component-business-table-status-booked-text-color)",
      show: true,
      footerColor: "var(--tea-component-business-table-status-booked-footer-bg-color)",
      activeBorderColor: "var(--tea-component-business-table-status-booked-active-shadow-color)",
    },
    {
      label: "维护中",
      value: 5,
      tableCount: 0,
      color: "var(--tea-component-business-table-status-maintenance-bg-color)",
      textColor: "var(--tea-component-business-table-status-maintenance-text-color)",
      show: false,
      footerColor: "var(--tea-component-business-table-status-maintenance-footer-bg-color)",
    },
    {
      label: "暂停中",
      value: 6,
      tableCount: 0,
      // color: "#909399",
      color: "var(--tea-component-business-table-status-paused-bg-color)",
      iconColor: "var(--tea-component-business-table-status-paused-accent-color)",
      textColor: "var(--tea-component-business-table-status-paused-text-color)",
      show: false,
      footerColor: "var(--tea-component-business-table-status-paused-footer-bg-color)",
      activeBorderColor: "var(--tea-component-business-table-status-paused-active-shadow-color)",
    },
    {
      label: "反结账",
      value: 7,
      tableCount: 0,
      color: "var(--tea-component-business-table-status-reverse-bg-color)",
      iconColor: "var(--tea-component-business-table-status-reverse-accent-color)",
      textColor: "var(--tea-component-business-table-status-reverse-text-color)",
      show: false,
      footerColor: "var(--tea-component-business-table-status-reverse-footer-bg-color)",
      activeBorderColor: "var(--tea-component-business-table-status-reverse-active-shadow-color)",
    },
  ])
  const selectProductListMap: Ref<Record<number, Product.MerchantTableProductVO[]>> = ref({})
  const tableCartSnapshotMap: Ref<Record<number, TableCartSnapshot | undefined>> = ref({})
  const tableCartSyncControllerMap = new Map<number, TableCartSyncController>()
  const categoryAndProductList = ref<Product.CategoryAndProductVO[]>([])
  const pmsItemExtraStockInfo = ref<{ [key: string]: number; }>({})
  const activatedIndex = ref(0)
  const openTableCategoryList = ref<Table.TableCategoryType[]>([])
  const exportData = ref()

  const setExportData = (value: any) => {
    exportData.value = value
  }

  const setTableCategoryList = (value: Table.TableCategoryType[]) => {
    tableCategoryList.value = value
  }

  const getTableCategoryList = () => {
    return tableCategoryList.value
  }

  const setOpenTableCategoryList = (value: Table.TableCategoryType[]) => {
    openTableCategoryList.value = value
  }

  const getOpeningTableListByCategoryId = (tableCategoryId: string) => {
    return computed(() => openingTableListMap.value[tableCategoryId] || [])
  }

  const getOpenTableCategoryList = () => {
    return openTableCategoryList.value
  }

  const getActivatedIndex = () => {
    return activatedIndex.value
  }

  const getSelectProductListByTableId = (tableId: number) => {
    const res = selectProductListMap.value[tableId] || []
    return res
  }

  const setOpeningTableList = (value: Table.MerchantOpeningTableDTO[]) => {
    openingTableList.value = [...value].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
  }

  const getOpeningTableList = () => {
    return openingTableList.value
  }

  const getOpeningTableVoByTableId = (tableId: number) =>
    computed(() => {
      return openingTableVoList.value.find((item) => item.tableId === tableId)!
    })

  const getOpeningTableInfoByTableId = (tableId: number) =>
    computed(() => {
      return openingTableList.value.find((item) => item.tableId === tableId)!
    })

  const getTableCategoryListAll = () => {
    return tableCategoryListAll
  }

  const getNewTableStatusList = (
    oldStatusList: Table.TableStatusType[],
    openTableList: Table.MerchantOpeningTableDTO[],
  ) => {
    return oldStatusList.map((item) => {
      let tableCount = 0
      if (item.value === 0) {
        tableCount = openTableList.length
      } else if (item.value === 4) {
        tableCount = openTableList.filter((table) => table.tableReservationInfo != null).length
      } else {
        tableCount = openTableList.filter((table) => table.status === item.value).length
      }
      return {
        ...item,
        tableCount,
      }
    })
  }

  const getTableStatusList = (tableCategoryId?: number) =>
    computed(() => {
      if (tableCategoryId === undefined) {
        return getNewTableStatusList(tableStatusList.value, openingTableList.value)
      } else {
        const targetTableList = openingTableListMap.value[tableCategoryId]
        return getNewTableStatusList(tableStatusList.value, targetTableList)
      }
    })

  /**
   * merchant/table/category/${shopId}
   */
  const fetchTableCategoryListById = async (shopId: number) => {
    const res = await getTableCategoryListByShopId(shopId)
    setTableCategoryList(res.data)
    return res.data
  }

  /**
   * merchant/table/opening/v2
   */
  const fetchOpeningTableListById = async () => {
    const res = await getOpeningTableListByShopId()
    const openingTables = normalizeOpeningTableListPayload(res.data)
    setOpeningTableList(openingTables)
    return openingTables
  }

  const setSelectProductListByTableId = (tableId: number, productList: Product.MerchantTableProductVO[]) => {
    selectProductListMap.value[tableId] = productList
  }

  const getTableCartSyncController = (tableId: number) => {
    let controller = tableCartSyncControllerMap.get(tableId)
    if (!controller) {
      controller = {
        generation: 0,
        serial: Promise.resolve(),
      }
      tableCartSyncControllerMap.set(tableId, controller)
    }
    return controller
  }

  const resolveCurrentTableRecordIdForCart = (tableId: number) => {
    const source = (openingTableVoList.value.find(item => item.tableId === tableId)
      ?? openingTableList.value.find(item => item.tableId === tableId)) as ((
      Table.MerchantOpeningTableVO | Table.MerchantOpeningTableDTO
    ) & {
      tableRecordFields?: { tableRecordId?: string | number | null } | null
    }) | undefined
    const value = source?.tableRecordId ?? source?.tableRecordFields?.tableRecordId
    return normalizeOpaqueLongId(value)
  }

  const applyTableCartSnapshot = (tableId: number, snapshot: TableCartSnapshot) => {
    const snapshotItems = cloneDeep(snapshot.items || [])
    tableCartSnapshotMap.value[tableId] = {
      ...snapshot,
      items: snapshotItems,
    }
    setSelectProductListByTableId(tableId, cloneDeep(snapshotItems))
    return tableCartSnapshotMap.value[tableId]!
  }

  const assertTableCartResponse = (res: IApiResponseData<TableCartSnapshot>) => {
    if (res.code !== 0 || !res.data) {
      throw new Error(res.msg || "购物车同步失败，请重试")
    }
    return res.data
  }

  const fetchSelectProductList = async (tableId: number, tableRecordId?: string | number) => {
    const activeTableRecordId = normalizeOpaqueLongId(
      tableRecordId ?? resolveCurrentTableRecordIdForCart(tableId),
    )
    if (!activeTableRecordId) {
      throw new Error("桌台状态已变化，请返回营业台刷新后重新下单")
    }
    const res = await getTableCartSnapshot(tableId, activeTableRecordId)
    const snapshot = applyTableCartSnapshot(tableId, assertTableCartResponse(res))
    return snapshot.items
  }

  const executePendingTableCartSync = (tableId: number) => {
    const controller = getTableCartSyncController(tableId)
    const batch = controller.pending
    if (!batch) {
      return
    }
    controller.pending = undefined
    if (batch.timer) {
      clearTimeout(batch.timer)
      batch.timer = null
    }

    const task = controller.serial
      .catch(() => undefined)
      .then(async () => {
        if (batch.generation !== controller.generation) {
          throw new Error("购物车同步任务已失效")
        }
        let currentSnapshot = tableCartSnapshotMap.value[tableId]
        if (!currentSnapshot
          || normalizeOpaqueLongId(currentSnapshot.tableRecordId) !== normalizeOpaqueLongId(batch.tableRecordId)) {
          await fetchSelectProductList(tableId, batch.tableRecordId)
          currentSnapshot = tableCartSnapshotMap.value[tableId]
        }
        if (!currentSnapshot) {
          throw new Error("购物车状态获取失败，请刷新后重试")
        }
        const res = await setTableCartSnapshot(tableId, {
          tableRecordId: batch.tableRecordId,
          scopeEpoch: currentSnapshot.scopeEpoch,
          items: cloneDeep(batch.productList),
        })
        const snapshot = assertTableCartResponse(res)
        if (batch.generation !== controller.generation) {
          throw new Error("购物车同步任务已失效")
        }
        return applyTableCartSnapshot(tableId, snapshot)
      })

    controller.serial = task.then(() => undefined, () => undefined)
    controller.active = task
    controller.activeTableRecordId = normalizeOpaqueLongId(batch.tableRecordId)
    task.finally(() => {
      if (controller.active === task) {
        controller.active = undefined
        controller.activeTableRecordId = undefined
      }
    }).catch(() => undefined)
    task.then(batch.resolve, batch.reject)
  }

  const scheduleTableCartSync = (
    tableId: number,
    tableRecordId: string | number,
    productList: Product.MerchantTableProductVO[],
    delay = 1000,
  ) => {
    const controller = getTableCartSyncController(tableId)
    const normalizedTableRecordId = normalizeOpaqueLongId(tableRecordId)
    if (!normalizedTableRecordId) {
      const rejected = Promise.reject<TableCartSnapshot>(new Error("桌台状态已变化，请刷新后重新下单"))
      rejected.catch(() => undefined)
      return rejected
    }
    const pendingTableRecordId = normalizeOpaqueLongId(controller.pending?.tableRecordId)
    const activeTableRecordId = controller.activeTableRecordId
    if ((pendingTableRecordId && pendingTableRecordId !== normalizedTableRecordId)
      || (activeTableRecordId && activeTableRecordId !== normalizedTableRecordId)) {
      cancelTableCartSync(tableId, true)
      const rejected = Promise.reject<TableCartSnapshot>(new Error("桌台状态已变化，请刷新后重新选择商品"))
      rejected.catch(() => undefined)
      return rejected
    }
    if (controller.pending) {
      controller.pending.tableRecordId = normalizedTableRecordId
      controller.pending.productList = cloneDeep(productList)
      if (controller.pending.timer) {
        clearTimeout(controller.pending.timer)
      }
      controller.pending.timer = setTimeout(() => executePendingTableCartSync(tableId), delay)
      return controller.pending.promise
    }

    let resolve!: (snapshot: TableCartSnapshot) => void
    let reject!: (error: unknown) => void
    const promise = new Promise<TableCartSnapshot>((resolvePromise, rejectPromise) => {
      resolve = resolvePromise
      reject = rejectPromise
    })
    promise.catch(() => undefined)
    const batch: TableCartSyncBatch = {
      generation: controller.generation,
      tableRecordId: normalizedTableRecordId,
      productList: cloneDeep(productList),
      timer: null,
      promise,
      resolve,
      reject,
    }
    controller.pending = batch
    batch.timer = setTimeout(() => executePendingTableCartSync(tableId), delay)
    return promise
  }

  const flushTableCartSync = async (
    tableId: number,
    tableRecordId?: string | number,
    productList?: Product.MerchantTableProductVO[],
  ) => {
    let scheduled: Promise<TableCartSnapshot> | undefined
    if (tableRecordId && productList) {
      scheduled = scheduleTableCartSync(tableId, tableRecordId, productList, 0)
    }
    const controller = getTableCartSyncController(tableId)
    const pending = controller.pending
    if (pending) {
      executePendingTableCartSync(tableId)
      await pending.promise
    } else if (controller.active) {
      await controller.active
    } else if (scheduled) {
      await scheduled
    } else {
      await controller.serial
    }
    let snapshot = tableCartSnapshotMap.value[tableId]
    if (!snapshot && tableRecordId) {
      await fetchSelectProductList(tableId, tableRecordId)
      snapshot = tableCartSnapshotMap.value[tableId]
    }
    if (!snapshot) {
      throw new Error("购物车状态获取失败，请刷新后重试")
    }
    return snapshot
  }

  const cancelTableCartSync = (tableId: number, clearState = false) => {
    const controller = getTableCartSyncController(tableId)
    controller.generation += 1
    const pending = controller.pending
    controller.pending = undefined
    if (pending?.timer) {
      clearTimeout(pending.timer)
    }
    if (pending) {
      pending.reject(new Error("购物车同步任务已取消"))
    }
    if (clearState) {
      delete tableCartSnapshotMap.value[tableId]
      setSelectProductListByTableId(tableId, [])
    }
  }

  const refreshTableCartSnapshotFromRemote = async (tableId: number, tableRecordId: string | number) => {
    const controller = getTableCartSyncController(tableId)
    if (controller.pending) {
      executePendingTableCartSync(tableId)
    }
    if (controller.active) {
      await controller.active
    } else {
      await controller.serial
    }
    return fetchSelectProductList(tableId, tableRecordId)
  }

  const getTableCartSnapshotByTableId = (tableId: number) => tableCartSnapshotMap.value[tableId]

  const completeTableCartOrder = async (tableId: number, tableRecordId: string | number) => {
    const snapshot = tableCartSnapshotMap.value[tableId]
    cancelTableCartSync(tableId)
    setSelectProductListByTableId(tableId, [])
    if (!snapshot || snapshot.scope === "EMPLOYEE_PRIVATE") {
      if (snapshot) {
        tableCartSnapshotMap.value[tableId] = {
          ...snapshot,
          revision: null,
          items: [],
        }
      }
      return
    }
    await flushTableCartSync(tableId, tableRecordId, [])
  }

  const invalidateTableCartsForScopeChange = () => {
    const tableIds = new Set([
      ...Object.keys(selectProductListMap.value).map(Number),
      ...Object.keys(tableCartSnapshotMap.value).map(Number),
      ...tableCartSyncControllerMap.keys(),
    ])
    tableIds.forEach(tableId => cancelTableCartSync(tableId, true))
  }

  /*
   * 立即更新桌台购物车商品数据
   * @param tableId 桌台id
   * @param productList 商品列表
   */
  const updateTableCartProductList = async (tableId: number, productList: Product.MerchantTableProductVO[]) => {
    const tableRecordId = resolveCurrentTableRecordIdForCart(tableId)
    if (!tableRecordId) {
      throw new Error("桌台状态已变化，请返回营业台刷新后重新下单")
    }
    return flushTableCartSync(tableId, tableRecordId, productList)
  }

  const getCategoryAndProductList = () => {
    return categoryAndProductList
  }

  // TODO 本机器或者其他机器修改商品\类目\预警信息等时，需要刷新
  const fetchBaseCategoryAndProductListPromise = ref<Promise<Product.ProductCategoryAndProductTListData> | null>(null)
  const fetchPmsItemExtraStockInfoPromise = ref<Promise<AxiosResponse<RMapLongInteger, any>> | null>(null)
  const effectiveCommissionEmployees = ref<CommissionEmployeeOption[]>([])
  const fetchEffectiveCommissionEmployeesPromise = ref<Promise<CommissionEmployeeOption[]> | null>(null)
  let commissionEmployeeShopId: number | undefined
  let commissionEmployeeGeneration = 0

  const canLoadEffectiveCommissionEmployees = () => {
    const allowed = userStore.checkPermission(BUSINESS_TABLE_ACTION_PERMISSIONS.itemCommission)
    if (!allowed || commissionEmployeeShopId !== userStore.shopId) {
      commissionEmployeeGeneration += 1
      effectiveCommissionEmployees.value = []
      fetchEffectiveCommissionEmployeesPromise.value = null
      commissionEmployeeShopId = userStore.shopId
    }
    return allowed
  }

  const fetchBaseCategoryAndProductList = async () => {
    const activeShopId = getActiveShopId()
    fetchBaseCategoryAndProductListPromise.value = getCategoryTreeWithProductListByShopId(activeShopId).catch((error) => {
      console.error("获取商品分类树失败，使用旧平铺分类商品接口兜底:", error)
      return getCategoryAndProductListByShopId(activeShopId)
    })
    const res = await fetchBaseCategoryAndProductListPromise.value
    const normalizedTree = normalizeProductCategoryTree<Product.MerchantPmsItem>(res.data ?? [])
    const sourceList = normalizedTree.length > 0
      ? normalizedTree
      : normalizeProductCategoryTree<Product.MerchantPmsItem>((await getCategoryAndProductListByShopId(activeShopId)).data ?? [])
    const secondCategories = flattenProductSecondCategories(sourceList)
    const allProductList = {
      categoryId: 0,
      shopId: activeShopId,
      categoryName: "全部",
      isShopDisplay: 1,
      sort: 0,
      categoryLevel: 1,
      children: secondCategories,
      productList: secondCategories.reduce((a: any, b: any) => a.concat(b.productList ?? []), []),
    }
    categoryAndProductList.value = [allProductList, ...sourceList] as Product.CategoryAndProductVO[]
    return res
  }

  const fetchPmsItemExtraStockInfo = async () => {
    fetchPmsItemExtraStockInfoPromise.value = merchantPmsItemCategoryAPI.merchantPmsItemCategoryGetPmsItemExtraStockInfo()
    const res = await fetchPmsItemExtraStockInfoPromise.value
    pmsItemExtraStockInfo.value = res.data.data ?? {}
    return res
  }

  const fetchEffectiveCommissionEmployees = () => {
    if (!canLoadEffectiveCommissionEmployees()) {
      return Promise.resolve<CommissionEmployeeOption[]>([])
    }
    const requestGeneration = commissionEmployeeGeneration
    const promise = getEffectiveCommissionEmployeeList()
      .then((res) => {
        // 切店或权限撤销后，旧上下文的请求不能继续返回员工。
        if (!canLoadEffectiveCommissionEmployees() || requestGeneration !== commissionEmployeeGeneration) {
          return []
        }
        const employees = res.code === 0 && res.data
          ? res.data
              .map((item) => ({
                id: item.merchantUserId,
                name: item.name,
              }))
              .filter((item): item is CommissionEmployeeOption => typeof item.id === "number" && Boolean(item.name))
          : []
        // 同店重叠刷新仍向原调用者返回数据，只由最新请求更新缓存。
        if (fetchEffectiveCommissionEmployeesPromise.value === promise) {
          effectiveCommissionEmployees.value = employees
        }
        return employees
      })
      .catch((error) => {
        if (fetchEffectiveCommissionEmployeesPromise.value === promise) {
          fetchEffectiveCommissionEmployeesPromise.value = null
        }
        throw error
      })
    fetchEffectiveCommissionEmployeesPromise.value = promise
    return promise
  }

  const ensureEffectiveCommissionEmployees = () => {
    if (!canLoadEffectiveCommissionEmployees()) {
      return Promise.resolve<CommissionEmployeeOption[]>([])
    }
    return fetchEffectiveCommissionEmployeesPromise.value ?? fetchEffectiveCommissionEmployees()
  }

  const getCurrentSelectProductList = (tableId?: number) =>
    computed(() => {
      if (tableId) {
        return getSelectProductListByTableId(tableId)
      } else {
        return []
      }
    })

  const setTableContentLoading = (value: boolean) => {
    tableContentLoading.value = value
  }

  const getTableContentLoading = () => {
    return tableContentLoading.value
  }

  // JZTODO 将Promise.all中两个方法的副作用去掉
  /**
   * merchant/table/category/${shopId}
   *
   * merchant/table/opening/v2
   */
  const fetchOpeningTableList = () => {
    return Promise.all([fetchTableCategoryListById(shopId), fetchOpeningTableListById()])
      .then(() => {
      })
      .catch((error) => {
        console.error('获取桌台列表失败:', error)
        throw error
      })
  }

  const fetchTableCategoryListV2Cache = () => {
    return getTableCategoryListV2().then((res) => {
      return res.data
    })
  }

  const clearTableCategoryListV2Cache = () => {
    tableCategoryListV2Cache.value = []
  }

  type TableRemarkSource = {
    remark?: string | null
    tableRecordFields?: {
      remark?: string | null
    } | null
  }

  const resolveTableRemark = (...sources: Array<TableRemarkSource | null | undefined>) => {
    for (const source of sources) {
      const remark = source?.remark ?? source?.tableRecordFields?.remark
      if (typeof remark === "string" && remark.trim()) {
        return remark.trim()
      }
    }
    return undefined
  }

  const isSameTableRecordId = (left?: number | string | null, right?: number | string | null) => {
    if (left == null || right == null) {
      return false
    }
    return String(left) === String(right)
  }

  type TableRecordIdentitySource = {
    tableRecordId?: number | string | null
    tableRecordFields?: {
      tableRecordId?: number | string | null
    } | null
  }

  const resolveTableRecordId = (source?: TableRecordIdentitySource | null) => {
    return source?.tableRecordId ?? source?.tableRecordFields?.tableRecordId
  }

  const setOpeningTablePrepaymentAmount = (tableRecordId: number | string, amount: number) => {
    const normalizedAmount = Number.isFinite(amount) ? Math.max(Math.round(amount), 0) : 0
    openingTableList.value.forEach((item) => {
      if (isSameTableRecordId(resolveTableRecordId(item), tableRecordId)) {
        Object.assign(item, { prepaymentAmount: normalizedAmount })
      }
    })
    openingTableVoList.value.forEach((item) => {
      if (isSameTableRecordId(resolveTableRecordId(item), tableRecordId)) {
        Object.assign(item, { prepaymentAmount: normalizedAmount })
      }
    })
  }

  let openingTableListRequestEpoch = 0
  let openingTableListLoadingOwnerEpoch: number | undefined
  const openingTableDetailRequestEpoch = new Map<number, number>()

  const beginOpeningTableListRequest = () => {
    openingTableListRequestEpoch += 1
    if (tableContentLoading.value) {
      openingTableListLoadingOwnerEpoch = openingTableListRequestEpoch
    }
    return openingTableListRequestEpoch
  }

  const isLatestOpeningTableListRequest = (requestEpoch: number) => {
    return openingTableListRequestEpoch === requestEpoch
  }

  const finishOpeningTableListRequest = (requestEpoch: number) => {
    if (openingTableListLoadingOwnerEpoch === requestEpoch) {
      openingTableListLoadingOwnerEpoch = undefined
      setTableContentLoading(false)
    }
  }

  const invalidateOpeningTableListRequests = () => {
    openingTableListRequestEpoch += 1
    if (openingTableListLoadingOwnerEpoch !== undefined) {
      openingTableListLoadingOwnerEpoch = undefined
      setTableContentLoading(false)
    }
  }

  const beginOpeningTableDetailRequest = (tableId: number) => {
    invalidateOpeningTableListRequests()
    const requestEpoch = (openingTableDetailRequestEpoch.get(tableId) ?? 0) + 1
    openingTableDetailRequestEpoch.set(tableId, requestEpoch)
    return requestEpoch
  }

  const isLatestOpeningTableDetailRequest = (tableId: number, requestEpoch: number) => {
    return openingTableDetailRequestEpoch.get(tableId) === requestEpoch
  }

  const normalizeDisplayMemberId = (value?: number | null) => {
    return typeof value === "number" && value > 0 ? value : undefined
  }

  const resolveCurrentTableMemberId = (
    nextMerchantUserId: number | null | undefined,
    previousMerchantUserId: number | null | undefined,
    previousTableRecordId: number | string | null | undefined,
    nextTableRecordId: number | string | null | undefined,
    hasNextMerchantUserId: boolean,
  ) => {
    if (hasNextMerchantUserId) {
      return nextMerchantUserId ?? undefined
    }
    return isSameTableRecordId(previousTableRecordId, nextTableRecordId)
      ? previousMerchantUserId ?? undefined
      : undefined
  }

  const resolveTableRelationFields = (
    tableConsumeDetail?: TableRelationSource | null,
    openingSameRecordInfo?: TableRelationSource | null,
  ): TableRelationSource => ({
    tableMovingRecordId: firstDefined(tableConsumeDetail?.tableMovingRecordId, openingSameRecordInfo?.tableMovingRecordId),
    tableRelationType: firstDefined(tableConsumeDetail?.tableRelationType, openingSameRecordInfo?.tableRelationType),
    tableRelationBadges: firstDefined(tableConsumeDetail?.tableRelationBadges, openingSameRecordInfo?.tableRelationBadges),
    tableTransferBatchList: mergeTableTransferBatchList(
      tableConsumeDetail?.tableTransferBatchList,
      openingSameRecordInfo?.tableTransferBatchList,
    ),
  })

  const fetchTableOpeningReservation = async () => {
    if (refreshReservationFlag.value) {
      try {
        const res = await getTableOpeningReservation()
        return {
          reservationList: normalizeTableOpeningReservationPayload(res.data, tableOpeningReservationList.value),
          shouldClearRefreshFlag: true,
        }
      } catch (error) {
        console.error('获取桌台预订信息失败:', error)
        throw error
      }
    } else {
      return Promise.resolve({
        reservationList: tableOpeningReservationList.value,
        shouldClearRefreshFlag: false,
      })
    }
  }

  /**
   * 更新桌台预订信息列表的副作用函数
   * @param reservationList 预订信息列表
   */
  const updateTableOpeningReservationList = (reservationList: Table.TableReservationDTO[]) => {
    tableOpeningReservationList.value = reservationList

    // 为桌台列表中的每个桌台匹配对应的预订信息
    openingTableList.value.forEach((tableItem) => {
      const matchedReservation = reservationList.find(
        (reservation) => reservation.tableId === tableItem.tableId,
      )

      if (matchedReservation) {
        tableItem.tableReservationInfo = matchedReservation
      } else if (tableItem.status === TableStatusConstants.TABLE_FREE) {
        tableItem.tableReservationInfo = undefined
      }
    })
  }

  const fetchOpeningTableListV2 = () => {
    resetRuntimeProjection()
    const requestEpoch = beginOpeningTableListRequest()
    if (isFirstLoad.value) {
      isFirstLoad.value = false
      setTableContentLoading(true)
      openingTableListLoadingOwnerEpoch = requestEpoch
    }
    const fetchConsumeDetailList = getOpeningTableConsumeDetailListByShopIdV2().catch((error) => {
      console.warn("获取桌台消费汇总失败，使用基础桌台列表兜底:", error)
      return { code: 0, msg: "", data: [] } as Awaited<ReturnType<typeof getOpeningTableConsumeDetailListByShopIdV2>>
    })
    return Promise.all([fetchTableCategoryListV2Cache(), fetchConsumeDetailList, fetchTableOpeningReservation(), getOpeningTableListByShopId()])
      .then(async (results) => {
        // 处理返回值
        const [categoryResult, tableConsumeResult, reservationResult, openingTableResult] = results
        if (!isLatestOpeningTableListRequest(requestEpoch)) {
          finishOpeningTableListRequest(requestEpoch)
          return { categoryResult, tableConsumeResult }
        }
        // tableList 包含动态桌台状态，营业页自动刷新必须使用实时返回值，不能复用旧缓存
        tableCategoryListV2Cache.value = cloneDeep(categoryResult)
        const tableCategoryList = categoryResult.map((item) => item.tableCategory)
        setTableCategoryList(tableCategoryList)
        const tableList = categoryResult.flatMap((item) => item.tableList)
        const openingTables = normalizeOpeningTableListPayload(openingTableResult?.data)
        const consumeDetails = Array.isArray(tableConsumeResult?.data) ? tableConsumeResult.data : []
        const openingTableMap = new Map(openingTables.map((item) => [item.tableId, item]))
        const consumeDetailMap = new Map(consumeDetails.map((detail) => [detail.tableId, detail]))
        const previousReservationMap = new Map(
          openingTableList.value.map((item) => [item.tableId, item.tableReservationInfo]),
        )
        const previousTableMap = new Map(openingTableList.value.map((item) => [item.tableId, item]))
        // 设置桌台列表数据
        openingTableList.value = tableList.map((item) => {
          const tableConsumeDetail = consumeDetailMap.get(item.tableId)
          const openingTableInfo = openingTableMap.get(item.tableId)
          // 消费汇总是辅助数据，取消/重开后可能短暂滞后；只允许当前桌台记录的汇总覆盖权威开台状态。
          const matchedConsumeDetail = tableConsumeDetail
            && isSameTableRecordId(resolveTableRecordId(openingTableInfo), tableConsumeDetail.tableRecordId)
            ? tableConsumeDetail
            : undefined
          const previousReservationInfo = previousReservationMap.get(item.tableId)
          const previousTableInfo = previousTableMap.get(item.tableId)
          if (matchedConsumeDetail) {
            const consumeCheckIn = matchedConsumeDetail.checkIn ? new Date(matchedConsumeDetail.checkIn) : undefined
            const openingSameRecordInfo = openingTableInfo
            const previousSameRecordInfo = isSameTableRecordId(resolveTableRecordId(previousTableInfo), matchedConsumeDetail.tableRecordId)
              ? previousTableInfo
              : undefined
            const mergedTable = {
              ...item,
              ...openingSameRecordInfo,
              ...matchedConsumeDetail,
              ...resolveTableRelationFields(matchedConsumeDetail, openingSameRecordInfo),
              prepaymentAmount: openingSameRecordInfo?.prepaymentAmount ?? 0,
              tableReservationInfo: item.tableReservationInfo ?? openingSameRecordInfo?.tableReservationInfo ?? previousReservationInfo,
              remark: resolveTableRemark(matchedConsumeDetail, openingSameRecordInfo, openingTableInfo, previousSameRecordInfo),
              tableBillingFields: (item as Table.MerchantOpeningTableDTO).tableBillingFields ?? openingSameRecordInfo?.tableBillingFields ?? previousSameRecordInfo?.tableBillingFields,
              billingSegments: openingSameRecordInfo?.billingSegments ?? previousSameRecordInfo?.billingSegments,
              billingActionState: openingSameRecordInfo?.billingActionState ?? previousSameRecordInfo?.billingActionState,
              billingVersion: openingSameRecordInfo?.billingVersion ?? previousSameRecordInfo?.billingVersion,
              packageCountdownInfo: matchedConsumeDetail.packageCountdownInfo !== undefined
                ? matchedConsumeDetail.packageCountdownInfo
                : openingSameRecordInfo?.packageCountdownInfo ?? previousSameRecordInfo?.packageCountdownInfo,
              checkIn: (item as Table.MerchantOpeningTableDTO).checkIn ?? consumeCheckIn ?? openingSameRecordInfo?.checkIn ?? previousSameRecordInfo?.checkIn,
            }
            const fallbackTotalFee = previousTableInfo
              && isSameTableRecordId(resolveTableRecordId(previousTableInfo), matchedConsumeDetail.tableRecordId)
              ? previousTableInfo.totalFee
              : undefined
            return {
              ...mergedTable,
              totalFee: getPreOpenCardTotalFee(mergedTable, fallbackTotalFee),
            }
          }
          // 以本轮刷新返回的基础桌台状态为准，避免沿用旧状态造成“已开台但费用/时长为空”的混合态
          return {
            ...item,
            ...openingTableInfo,
            tableReservationInfo: item.tableReservationInfo ?? openingTableInfo?.tableReservationInfo ?? previousReservationInfo,
            remark: resolveTableRemark(openingTableInfo, previousTableInfo),
          }
        }).sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)) || []
        if (reservationResult.shouldClearRefreshFlag) {
          setRefreshReservationFlag(false)
        }
        updateTableOpeningReservationList(reservationResult.reservationList)
        finishOpeningTableListRequest(requestEpoch)
        await fetchRuntimeProjection(true)
        return { categoryResult, tableConsumeResult }
      })
      .catch((error) => {
        console.error('获取桌台列表失败:', error)
        finishOpeningTableListRequest(requestEpoch)
        throw error
      })
  }

  const fetchOpeningTableInfoById = async (tableId: number) => {
    resetRuntimeProjection()
    const requestEpoch = beginOpeningTableDetailRequest(tableId)
    const res = await getOpeningTableInfoV2(tableId)
    if (!isLatestOpeningTableDetailRequest(tableId, requestEpoch)) {
      return
    }
    invalidateOpeningTableListRequests()
    const currentTableInfo = openingTableList.value.find((item) => item.tableId === tableId)
    const currentSameRecordInfo = isSameTableRecordId(currentTableInfo?.tableRecordId, res.data.tableRecordId)
      ? currentTableInfo
      : undefined
    const hasDetailMemberField = Object.prototype.hasOwnProperty.call(res.data, "merchantUserId")
    const detailMemberId = normalizeDisplayMemberId(res.data.merchantUserId)
    // `0`/`null` 是后端明确的退出会员结果；只有字段真的缺失时，才允许用列表数据兜底。
    const fallbackMemberId = hasDetailMemberField
      ? undefined
      : normalizeDisplayMemberId(currentSameRecordInfo?.merchantUserId)
    const mergedDetail = {
      ...res.data,
      displayMerchantUserId: detailMemberId ?? fallbackMemberId,
      displayMerchantUserIdSource: detailMemberId
        ? "opening-info"
        : fallbackMemberId ? "opening-list-fallback" : undefined,
    }
    const reservationInfo = res.data.tableReservationInfo || currentTableInfo?.tableReservationInfo

    const index = openingTableVoList.value.findIndex((item) => item.tableId === tableId)
    openingTableList.value.forEach((item) => {
      if (item.tableId === tableId) {
        const previousTableRecordId = item.tableRecordId
        const previousMerchantUserId = item.merchantUserId
        const sameTableRecord = isSameTableRecordId(previousTableRecordId, mergedDetail.tableRecordId)
        const recordScopedItem = item as Table.MerchantOpeningTableDTO & {
          tableConsumeInfoList?: Table.TableConsumeInfo[]
          tableRecordFields?: Record<string, unknown> | null
        }
        const tableBillingFields = (mergedDetail as Table.MerchantOpeningTableVO & {
          tableBillingFields?: Table.MerchantTableBillingFields
        }).tableBillingFields
        const {
          status,
          tableRecordId,
          stayMinutes,
          totalFee,
          customerNumber,
          preCheckoutFlag,
          preOpenFlag,
          preCheckoutTime,
          isScan,
          merchantUserId,
          remark,
        } = mergedDetail
        const checkIn = mergedDetail.tableConsumeInfoList?.[0]?.checkIn

        item.status = status
        item.tableRecordId = tableRecordId || undefined
        item.stayMinutes = stayMinutes ?? undefined
        item.checkIn = checkIn ? new Date(checkIn) : sameTableRecord ? item.checkIn : undefined
        item.totalFee = getPreOpenCardTotalFee(res.data) ?? totalFee ?? undefined
        item.customerNumber = customerNumber ?? undefined
        item.preCheckoutFlag = preCheckoutFlag ?? undefined
        item.preOpenFlag = preOpenFlag ?? undefined
        item.preCheckout = preCheckoutTime ? new Date(preCheckoutTime) : undefined
        item.isScan = isScan ?? item.isScan
        item.merchantUserId = resolveCurrentTableMemberId(
          merchantUserId,
          previousMerchantUserId,
          previousTableRecordId,
          tableRecordId,
          hasDetailMemberField,
        )
        item.remark = remark ?? item.remark
        item.tableBillingFields = sameTableRecord ? tableBillingFields ?? item.tableBillingFields : tableBillingFields
        item.billingSegments = sameTableRecord ? mergedDetail.billingSegments ?? item.billingSegments : mergedDetail.billingSegments
        item.billingActionState = sameTableRecord ? mergedDetail.billingActionState ?? item.billingActionState : mergedDetail.billingActionState
        item.billingVersion = sameTableRecord ? mergedDetail.billingVersion ?? item.billingVersion : mergedDetail.billingVersion
        recordScopedItem.tableConsumeInfoList = sameTableRecord
          ? mergedDetail.tableConsumeInfoList ?? recordScopedItem.tableConsumeInfoList
          : mergedDetail.tableConsumeInfoList
        item.tableReservationInfo = reservationInfo

        const detailIsPreOpen = isPreOpenTable(mergedDetail)

        // 单桌刷新在关台后要清理旧消费字段，避免“状态已变更但费用/时长仍沿用旧值”的混合态。
        // 预开台详情可能仍保留基础桌台 status=1，此时要以 preOpenFlag/tableRecordId 为准保留预消费状态。
        if (!item.tableRecordId || (status === 1 && !detailIsPreOpen)) {
          item.tableRecordId = undefined
          recordScopedItem.tableRecordFields = undefined
          recordScopedItem.tableConsumeInfoList = undefined
          item.totalFee = undefined
          item.stayMinutes = undefined
          item.lastPauseSeconds = undefined
          item.pauseRecordList = undefined
          item.orderFlag = undefined
          item.checkIn = undefined
          item.customerNumber = undefined
          item.preCheckoutFlag = undefined
          item.preOpenFlag = undefined
          item.preCheckout = undefined
          item.merchantUserId = undefined
          item.remark = undefined
          item.tableBillingFields = undefined
          item.billingSegments = undefined
          item.billingActionState = undefined
          item.billingVersion = undefined
          item.packageCountdownInfo = undefined
          item.tableReservationInfo = reservationInfo
        }
      }
    })
    mergedDetail.tableReservationInfo = reservationInfo
    if (index !== -1) {
      openingTableVoList.value[index] = mergedDetail
    } else {
      openingTableVoList.value.push(mergedDetail)
    }
  }

  /**
   * 根据tableId更新桌台信息，会保留原来的值
   * @param tableId 桌台id
   */
  const refreshTableById = async (tableId: number) => {
    try {
      await fetchOpeningTableInfoById(tableId)
    } catch (error) {
      console.error('刷新桌台信息失败:', error)
      throw error
    }
  }

  const setActiveStatus = (value: number) => {
    activeStatus.value = value
  }

  const getActiveStatus = () => {
    return activeStatus
  }

  return {
    addRefreshTableMsg,
    getRefreshTableMsgList,
    getConsumeDetail,
    getConsumeDetailRecord,
    fetchOpeningTableComsumeDetailByRecord,
    tableContentLoading,
    setTableContentLoading,
    fetchBaseCategoryAndProductList,
    fetchBaseCategoryAndProductListPromise,
    fetchPmsItemExtraStockInfo,
    fetchPmsItemExtraStockInfoPromise,
    effectiveCommissionEmployees,
    fetchEffectiveCommissionEmployees,
    fetchEffectiveCommissionEmployeesPromise,
    ensureEffectiveCommissionEmployees,
    getTableCategoryList,
    setTableCategoryList,
    getTableStatusList,
    fetchOpeningTableList,
    fetchOpeningTableListV2,
    refreshRuntimeOrLegacy,
    applyRuntimeMessage,
    resetRuntimeProjection,
    getOpeningTableList,
    getOpeningTableVoByTableId,
    fetchOpeningTableInfoById,
    getOpeningTableInfoByTableId,
    getTableCategoryListAll,
    setOpeningTableList,
    setOpeningTablePrepaymentAmount,
    refreshTableById,
    updateTableCartProductList,
    fetchSelectProductList,
    setSelectProductListByTableId,
    scheduleTableCartSync,
    flushTableCartSync,
    cancelTableCartSync,
    refreshTableCartSnapshotFromRemote,
    getTableCartSnapshotByTableId,
    completeTableCartOrder,
    invalidateTableCartsForScopeChange,
    getCategoryAndProductList,
    getCurrentSelectProductList,
    fetchTableCategoryListById,
    fetchOpeningTableListById,
    getActivatedIndex,
    getSelectProductListByTableId,
    setOpenTableCategoryList,
    getOpeningTableListByCategoryId,
    getOpenTableCategoryList,
    clearTableCategoryListV2Cache,
    setRefreshReservationFlag,
    setExportData,
    exportData,
    fetchOpeningTableConsumeDetailList,
    getOpeningTableConsumeDetailList,
    getOpeningTableConsumeDetailByTableRecordId,
    getOpeningTableTotalFee,
    getOpeningTableOrderFlag,
    setActiveStatus,
    getActiveStatus,
    setRefreshCount,
    getRefreshCount,
    openTableDialogVisible,
    currentOpenTableInfo,
    tableReservationFields,
    handleOpenTable,
    enhancedDebounceRefresh,
    getTableContentLoading,
  }
})
